<template>
  <div
    class="sticky-cta"
    :class="{ 'is-visible': shown }"
    :aria-hidden="shown ? 'false' : 'true'"
  >
    <div class="sticky-cta-inner">
      <span v-if="priceLabel" class="sticky-cta-price">{{ priceLabel }}</span>
      <span v-else class="sticky-cta-price"></span>
      <button
        type="button"
        class="sticky-cta-btn"
        :tabindex="shown ? 0 : -1"
        @pointerdown="prefetchBookingSheet"
        @click="openBookingSheet({ source: 'sticky' })"
      >
        {{ $t('services.bookNow') }}
      </button>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import { useServices } from '@/composables/useServices'
import { useBookingSheet } from '@/composables/useBookingSheet'

export default {
  name: 'StickyCtaBar',
  setup() {
    const { t } = useI18n()
    const { formatCurrencyWithSymbol, fetchSystemPreferences } = useCurrency()
    const { cheapestService, load: loadServices } = useServices()
    const {
      isOpen: isSheetOpen,
      open: openBookingSheet,
      prefetch: prefetchBookingSheet
    } = useBookingSheet()

    // Becomes true once the hero CTA has scrolled out of the viewport.
    const heroCtaOut = ref(false)
    let observer = null

    const shown = computed(() => heroCtaOut.value && !isSheetOpen.value)

    const priceLabel = computed(() => {
      const service = cheapestService.value
      if (!service) return ''
      return t('cta.fromPrice', {
        price: formatCurrencyWithSymbol(service.price),
        minutes: service.duration
      })
    })

    onMounted(() => {
      loadServices()
      fetchSystemPreferences()

      const heroCta = document.getElementById('hero-cta')
      if (!heroCta || typeof IntersectionObserver === 'undefined') {
        // No hero CTA to track (or no observer support) — always offer the bar.
        heroCtaOut.value = true
        return
      }

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) heroCtaOut.value = !entry.isIntersecting
        },
        { threshold: 0 }
      )
      observer.observe(heroCta)
    })

    onBeforeUnmount(() => {
      if (observer) observer.disconnect()
      observer = null
    })

    return {
      shown,
      priceLabel,
      openBookingSheet,
      prefetchBookingSheet
    }
  }
}
</script>

<style scoped>
.sticky-cta {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 998;
  background: var(--surface-card);
  border-top: 1px solid var(--border-light);
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.08);
  padding: 0 var(--gutter) env(safe-area-inset-bottom);
  transform: translateY(100%);
  visibility: hidden;
  transition: transform 0.25s ease, visibility 0.25s ease;
}

.sticky-cta.is-visible {
  transform: translateY(0);
  visibility: visible;
}

.sticky-cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-3);
  min-height: 64px;
}

.sticky-cta-price {
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
}

.sticky-cta-btn {
  min-height: 48px;
  padding: 0 var(--s-5);
  border: none;
  border-radius: 50px;
  /* Solid CTA colour to match the hero primary CTA — see HeroSection. */
  background: var(--cta-bg);
  color: var(--cta-fg);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: var(--shadow-brand);
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}

.sticky-cta-btn:active {
  transform: scale(0.98);
}

.sticky-cta-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* Mobile only — desktop keeps the header CTA */
@media (min-width: 768px) {
  .sticky-cta {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sticky-cta {
    transition: none;
  }

  .sticky-cta-btn:active {
    transform: none;
  }
}
</style>
